/**
 * Product Definitions tab.
 *
 * Read-only inspector for the shared product-terms registry. Backend serves
 * `GET /v1/admin/product-definitions?league=…`; the source of truth lives in
 * `shared/product-definitions.ts`.
 *
 * The tab exists so admins (and product/QA) can audit named UI concepts —
 * captaincy, GW Wrapped, Score DNA, FDR, etc. — without reading frontend
 * rendering code. It surfaces scope (EPL/WC/both), status, owner, data
 * sources, the plain-English formula, and any caveats flagged for admins.
 */

import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';

import {
  useProductDefinitions,
  type DefinitionOwner,
  type DefinitionScope,
  type DefinitionStatus,
  type LeagueName,
  type ProductDefinition,
} from './api';
import { DataTableShell } from './shared';

export function DefinitionsTab({ league }: { league: LeagueName }) {
  const { data, isLoading, error } = useProductDefinitions(league);
  const rows = data?.data ?? [];

  if (isLoading || error || rows.length === 0) {
    return (
      <DataTableShell
        loading={isLoading}
        error={error}
        empty={rows.length === 0}
        emptyLabel="No product definitions registered yet"
      >
        <div />
      </DataTableShell>
    );
  }

  return (
    <div className="space-y-3">
      <p className="text-xs text-muted-foreground">
        Glossary of user-facing product terms and derived UI concepts. Showing definitions whose
        scope is <b>{league}</b> or <b>both leagues</b>. Source of truth:{' '}
        <code className="font-mono">shared/product-definitions.ts</code>.
      </p>

      <div className="grid gap-3">
        {rows.map((row) => (
          <DefinitionCard key={row.term} def={row} />
        ))}
      </div>
    </div>
  );
}

function DefinitionCard({ def }: { def: ProductDefinition }) {
  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex flex-wrap items-baseline justify-between gap-3">
          <div>
            <CardTitle className="text-base">{def.audience_label}</CardTitle>
            <div className="mt-1 font-mono text-xs text-muted-foreground">{def.term}</div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <ScopeChip scope={def.scope} />
            <StatusChip status={def.status} />
            <OwnerChip owner={def.owner} />
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-3 pt-0 text-sm">
        <p className="text-foreground">{def.short_definition}</p>
        <p className="text-muted-foreground">{def.long_definition}</p>

        <FieldBlock label="Calculation notes">
          <p className="font-mono text-xs leading-relaxed text-foreground/90">
            {def.calculation_notes}
          </p>
        </FieldBlock>

        <FieldBlock label="Data sources">
          <ul className="space-y-1 text-xs">
            {def.data_sources.map((src) => (
              <li key={src} className="font-mono text-muted-foreground break-all">
                {src}
              </li>
            ))}
          </ul>
        </FieldBlock>

        {def.surfaces && def.surfaces.length > 0 ? (
          <FieldBlock label="Where this surfaces">
            <ul className="space-y-1 text-xs">
              {def.surfaces.map((surface) => (
                <li key={surface} className="text-muted-foreground">
                  {surface}
                </li>
              ))}
            </ul>
          </FieldBlock>
        ) : null}

        {def.admin_notes ? (
          <FieldBlock label="Admin notes">
            <p className="rounded-md border border-amber-500/30 bg-amber-500/5 px-3 py-2 text-xs leading-relaxed text-amber-700 dark:text-amber-300">
              {def.admin_notes}
            </p>
          </FieldBlock>
        ) : null}
      </CardContent>
    </Card>
  );
}

function FieldBlock({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <div className="mb-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        {label}
      </div>
      {children}
    </div>
  );
}

function ScopeChip({ scope }: { scope: DefinitionScope }) {
  const label = scope === 'BOTH' ? 'EPL + WC' : scope;
  return (
    <Badge variant="outline" className="text-xs">
      {label}
    </Badge>
  );
}

function StatusChip({ status }: { status: DefinitionStatus }) {
  const tone =
    status === 'stable'
      ? 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-300'
      : status === 'experimental'
        ? 'bg-amber-500/15 text-amber-700 dark:text-amber-300'
        : 'bg-muted text-muted-foreground';
  const label = status.replace(/_/g, ' ');
  return <span className={`rounded-full px-2 py-0.5 text-xs ${tone}`}>{label}</span>;
}

function OwnerChip({ owner }: { owner: DefinitionOwner }) {
  return (
    <span className="rounded-full border px-2 py-0.5 text-xs text-muted-foreground">
      owner: {owner}
    </span>
  );
}
